import { QueryClientProvider } from "@tanstack/react-query";
import type { ComponentPropsWithoutRef } from "react";
import "whatwg-fetch";
import { formatCurrency } from "../intl_helpers";
import { queryClient } from "../query";
import type { Account } from "../types";
import { route } from "../urls";
import { useInvoicesQuery } from "./invoices";

export default function BillingSummary(
  props: ComponentPropsWithoutRef<typeof BillingSummaryInner>
) {
  return (
    <QueryClientProvider client={queryClient}>
      <BillingSummaryInner {...props} />
    </QueryClientProvider>
  );
}

export function BillingSummaryInner({
  account,
  linkToInvoices = false,
}: {
  account: Account;
  linkToInvoices?: boolean;
}) {
  const query = useInvoicesQuery(account);

  const viewInvoicesNode = linkToInvoices ? (
    <a
      href={`${route("account_billing_settings")}#invoices`}
      className="fade-in"
    >
      View your invoices.
    </a>
  ) : null;

  if (query.isError) {
    return (
      <p className="text-r8-red-10">
        Sorry, we were unable to load your billing summary. {viewInvoicesNode}
      </p>
    );
  }

  if (query.isPending) {
    return <p>Your usage so far this month has a total cost of...</p>;
  }

  const { invoices, unused_credit } = query.data;

  const totalCost = invoices
    .filter(
      (invoice) =>
        !invoice.ended_before || Date.parse(invoice.ended_before) > Date.now()
    )
    .map((invoice) => Number.parseFloat(invoice.total_cost || "0"))
    .reduce((total, invoiceTotal) => total + invoiceTotal, 0);

  const creditLeft = Number.parseFloat(unused_credit || "0");

  return (
    <p>
      Your usage so far this month has a total cost of{" "}
      <span className="fade-in">
        {formatCurrency(totalCost)}.{" "}
        {creditLeft ? (
          <span>You have {formatCurrency(creditLeft)} credit remaining.</span>
        ) : null}
      </span>{" "}
      {viewInvoicesNode}
    </p>
  );
}
